import React, { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  type FiltersDefinition,
  GroupingDefinition,
  PaginatedResponse,
} from '@factorialco/f0-react'
import {
  DataCollectionPaginatedFetchOptions,
  type ItemActionsDefinition,
  type NavigationFiltersDefinition,
  OneDataCollection,
  type SortingsDefinition,
  type SummariesDefinition,
  useDataCollectionSource,
} from '@factorialco/f0-react/dist/experimental'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import {
  type GroupedData,
  type NodeChildren,
  TreeNode,
  getChildNodeType,
  getChildTypeText,
  getOverviewEmptyStateContent,
  getParentChain,
  getSalariesFromChildAttributes,
  maximizeClickPath,
  normalizeNodeType,
} from 'modules/job_catalog/lib/nodesUtils'
import { formatSalariesWithFrequency } from 'modules/job_catalog/lib/salaryUtils'

interface Props {
  currentNode: TreeNode
  childrenInformation: NodeChildren[]
}

const buildDataFromChildren = (childrenInformation: NodeChildren[]): GroupedData[] => {
  return childrenInformation.map((child) => ({
    name: child.name,
    uuid: child.id.toString(),
    salary:
      formatSalariesWithFrequency(getSalariesFromChildAttributes(child.attributesJson)) || '-',
  }))
}

const OverviewAttributes = ({ currentNode, childrenInformation }: Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const data = useMemo(() => buildDataFromChildren(childrenInformation), [childrenInformation])

  const childType = useMemo(() => getChildNodeType(currentNode.node_uuid), [currentNode.node_uuid])
  const normalizedChildType = useMemo(() => normalizeNodeType(childType), [childType])

  const columns = useMemo(() => {
    const baseColumns = []

    const nameColumnLabel = getChildTypeText(currentNode.node_uuid) || ''

    baseColumns.push({
      label: nameColumnLabel,
      render: (i: GroupedData) => ({
        type: 'text' as const,
        value: (i.name || '').length > 30 ? `${(i.name || '').slice(0, 30)}...` : i.name || '',
      }),
    })

    baseColumns.push({
      label: i18n.t('job_catalog.nodes.salary'),
      render: (i: GroupedData) => ({
        type: 'text' as const,
        value: i.salary || '-',
      }),
    })

    return baseColumns
  }, [currentNode.node_uuid])

  const source = useDataCollectionSource<
    GroupedData,
    FiltersDefinition<string>,
    SortingsDefinition,
    SummariesDefinition,
    ItemActionsDefinition<GroupedData>,
    NavigationFiltersDefinition,
    GroupingDefinition<GroupedData>
  >({
    dataAdapter: {
      fetchData: ({
        pagination,
      }: DataCollectionPaginatedFetchOptions<
        FiltersDefinition<string>,
        NavigationFiltersDefinition
      >): Promise<PaginatedResponse<GroupedData>> => {
        const currentPage = pagination?.currentPage || 1
        const perPage = 10
        const start = (currentPage - 1) * perPage
        const end = start + perPage
        const paginatedData = data.slice(start, end)

        return Promise.resolve({
          type: 'pages',
          records: paginatedData,
          total: data.length,
          currentPage,
          perPage,
          pagesCount: Math.ceil(data.length / perPage),
        })
      },
      perPage: 10,
      paginationType: 'pages',
    },
  })

  return (
    <OneDataCollection
      source={source}
      emptyStates={{
        'no-data': getOverviewEmptyStateContent({
          type: normalizedChildType,
          handleAction: () =>
            navigate(
              maximizeClickPath(
                getParentChain(currentNode),
                normalizeNodeType(currentNode.type),
                currentNode.node_uuid,
                navigation,
                currentNode.type
              )
            ),
        }),
      }}
      visualizations={[
        {
          type: 'table',
          options: { columns, frozenColumns: 0 },
        },
      ]}
    />
  )
}

export default OverviewAttributes
